import React, { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Button } from '../components/Button';
import { SelectableCard } from '../components/SelectableCard';
import { Input } from '../components/Input';
interface OnboardingStep2Props {
  onBack: () => void;
  onComplete: () => void;
}
export function OnboardingStep2({
  onBack,
  onComplete
}: OnboardingStep2Props) {
  const [q1, setQ1] = useState('');
  const [q2, setQ2] = useState('');
  const [q3, setQ3] = useState('');
  const [q4, setQ4] = useState('');
  const [q5, setQ5] = useState('');
  const [q6, setQ6] = useState('');
  // Optional weights
  const [legPress, setLegPress] = useState('');
  const [latPulldown, setLatPulldown] = useState('');
  const [dumbbellPress, setDumbbellPress] = useState('');
  const [hipThrust, setHipThrust] = useState('');
  const showWeights = q5 === 'Yes, roughly' || q5 === 'Yes, I remember them well';
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onComplete();
  };
  return <div className="flex flex-col h-full w-full bg-stone-50 overflow-hidden" data-id="element-824">
      {/* Header */}
      <div className="flex items-center justify-between p-6 pt-8 bg-stone-50/90 backdrop-blur-md z-20 shrink-0" data-id="element-825">
        <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-slate-200/50 text-slate-600 transition-colors" aria-label="Go back" data-id="element-826">
          <ChevronLeft size={28} data-id="element-827" />
        </button>
        <div className="w-12 h-12 rounded-2xl overflow-hidden border-2 border-white shadow-sm bg-stone-200" data-id="element-828">
          <video autoPlay loop muted playsInline className="w-full h-full object-cover" src="https://cdn.magicpatterns.com/uploads/69mUG6ooMsKbP89Ta37mkt/Katethought_friendly_gym_mascot_character_funny_good_guy_musc_bc3cfd10-111a-4766-8e76-81af2852edc8_2.mp4" data-id="element-829" />
        </div>
        <div className="w-10" data-id="element-830" /> {/* Spacer */}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-6 pb-32" data-id="element-831">
        <div className="mb-8 mt-2" data-id="element-832">
          <span className="text-sm font-extrabold text-emerald-500 tracking-wider uppercase mb-2 block" data-id="element-833">
            Step 2 of 2
          </span>
          <h1 className="text-3xl font-extrabold text-slate-800 mb-2 uppercase tracking-tight" data-id="element-834">
            Quick Calibration
          </h1>
          <p className="text-slate-500 font-medium leading-relaxed" data-id="element-835">
            Help us choose a safer and more realistic starting point for your
            first week.
          </p>
        </div>

        <form id="onboarding2-form" onSubmit={handleSubmit} className="space-y-12" data-id="element-836">
          {/* Q1 */}
          <div className="space-y-4" data-id="element-837">
            <h3 className="font-bold text-slate-700 text-lg leading-snug" data-id="element-838">
              Have you trained with gym machines or free weights before?
            </h3>
            <div className="space-y-2" data-id="element-839">
              {['Never', 'A few times', 'On and off', 'Regularly'].map(opt => <SelectableCard key={opt} label={opt} selected={q1 === opt} onClick={() => setQ1(opt)} data-id="element-840" />)}
            </div>
          </div>

          {/* Q2 */}
          <div className="space-y-4" data-id="element-841">
            <h3 className="font-bold text-slate-700 text-lg leading-snug" data-id="element-842">
              How familiar are you with these movements: squats, lunges, bench
              press, deadlift, or Romanian deadlift?
            </h3>
            <div className="space-y-2" data-id="element-843">
              {['I don’t know them', 'I’ve seen them but never really done them', 'I’ve done some of them', 'I’m comfortable with most of them'].map(opt => <SelectableCard key={opt} label={opt} selected={q2 === opt} onClick={() => setQ2(opt)} data-id="element-844" />)}
            </div>
          </div>

          {/* Q3 */}
          <div className="space-y-4" data-id="element-845">
            <h3 className="font-bold text-slate-700 text-lg leading-snug" data-id="element-846">
              Can you do 10 bodyweight squats with control and confidence?
            </h3>
            <div className="space-y-2" data-id="element-847">
              {['No', 'Not sure', 'Yes, but it feels difficult', 'Yes, comfortably'].map(opt => <SelectableCard key={opt} label={opt} selected={q3 === opt} onClick={() => setQ3(opt)} data-id="element-848" />)}
            </div>
          </div>

          {/* Q4 */}
          <div className="space-y-4" data-id="element-849">
            <h3 className="font-bold text-slate-700 text-lg leading-snug" data-id="element-850">
              Which variation of a push-up can you do with control?
            </h3>
            <div className="space-y-2" data-id="element-851">
              {['I can’t do push-ups yet', 'Wall push-up', 'Bench / incline push-up', 'Floor push-up'].map(opt => <SelectableCard key={opt} label={opt} selected={q4 === opt} onClick={() => setQ4(opt)} data-id="element-852" />)}
            </div>
          </div>

          {/* Q5 */}
          <div className="space-y-4" data-id="element-853">
            <h3 className="font-bold text-slate-700 text-lg leading-snug" data-id="element-854">
              Do you remember using any training weights before?
            </h3>
            <div className="space-y-2" data-id="element-855">
              {['No, never', 'I trained before but don’t remember the weights', 'Yes, roughly', 'Yes, I remember them well'].map(opt => <SelectableCard key={opt} label={opt} selected={q5 === opt} onClick={() => setQ5(opt)} data-id="element-856" />)}
            </div>
          </div>

          {/* Optional Weights */}
          {showWeights && <div className="p-6 bg-blue-50/80 border-2 border-blue-200 rounded-3xl space-y-5 animate-in fade-in slide-in-from-top-2 shadow-sm" data-id="element-857">
              <div data-id="element-858">
                <span className="inline-block px-2.5 py-1 bg-blue-100 text-blue-700 text-[10px] font-extrabold uppercase tracking-wider rounded-lg mb-2" data-id="element-859">
                  Optional
                </span>
                <p className="text-sm text-slate-700 font-bold leading-relaxed" data-id="element-860">
                  Tell us any weights you remember (kg/lbs) to help us calibrate
                  your first week perfectly.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-861">
                <Input label="Leg press" type="number" placeholder="e.g. 50" value={legPress} onChange={e => setLegPress(e.target.value)} data-id="element-862" />
                <Input label="Lat pulldown" type="number" placeholder="e.g. 30" value={latPulldown} onChange={e => setLatPulldown(e.target.value)} data-id="element-863" />
                <Input label="Dumbbell press (each)" type="number" placeholder="e.g. 15" value={dumbbellPress} onChange={e => setDumbbellPress(e.target.value)} data-id="element-864" />
                <Input label="Hip thrust / deadlift" type="number" placeholder="e.g. 60" value={hipThrust} onChange={e => setHipThrust(e.target.value)} data-id="element-865" />
              </div>
            </div>}

          {/* Q6 */}
          <div className="space-y-4 pb-4" data-id="element-866">
            <h3 className="font-bold text-slate-700 text-lg leading-snug" data-id="element-867">
              How would you describe your gym training background?
            </h3>
            <div className="space-y-2" data-id="element-868">
              {['Brand new to the gym', 'I trained a little before', 'I trained regularly before'].map(opt => <SelectableCard key={opt} label={opt} selected={q6 === opt} onClick={() => setQ6(opt)} data-id="element-869" />)}
            </div>
          </div>
        </form>
      </div>

      {/* Sticky Footer */}
      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-stone-50 via-stone-50 to-transparent pt-12 z-20 pointer-events-none" data-id="element-870">
        <div className="pointer-events-auto" data-id="element-871">
          <Button type="submit" form="onboarding2-form" variant="primary" fullWidth disabled={!q1 || !q2 || !q3 || !q4 || !q5 || !q6} data-id="element-872">
            Create my starting program
          </Button>
        </div>
      </div>
    </div>;
}